/**
 * ResourceEditor error mapping helpers
 *
 * Pure functions that map a React Hook Form `FieldErrors` tree onto the flat
 * dotted field paths owned by each ResourceEditor tab. Used to:
 *  - render a per-tab error badge (`tabHasError`)
 *  - jump to the first offending tab on an invalid submit (`firstTabWithError`)
 *
 * These are intentionally framework-light (no React, no DOM) so they can be
 * unit tested in isolation with bun:test.
 */

import type { FieldErrors, FieldValues } from 'react-hook-form';
import { z } from 'zod';

/** Minimal shape of a tab needed for error mapping. */
export interface ErrorMappableTab {
  /** Stable tab id. */
  id: string;
  /**
   * RHF field paths owned by this tab. Each entry matches an error path either
   * exactly or as a dotted prefix (e.g. `'metadata'` matches `'metadata.userAgent'`,
   * `'routingConfig.endpoints'` matches `'routingConfig.endpoints.0.host'`).
   */
  fields: ReadonlyArray<string>;
  /** Hidden tabs are skipped — the user cannot navigate to them to fix anything. */
  hidden?: boolean;
}

/**
 * Keys that identify a React Hook Form leaf `FieldError` object rather than a
 * nested container of further errors. RHF leaf errors carry `message`/`type`
 * (and a DOM `ref`); containers are plain objects/arrays keyed by field name.
 */
const FIELD_ERROR_MARKER_KEYS = ['message', 'type', 'ref', 'types'] as const;

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** True when `value` looks like an RHF leaf FieldError (vs. a nested container). */
function isFieldErrorLeaf(value: unknown): boolean {
  if (!isPlainRecord(value)) return false;
  return FIELD_ERROR_MARKER_KEYS.some((key) => key in value);
}

/**
 * Flatten an RHF error tree into a list of dotted leaf paths.
 *
 * Examples of produced paths:
 *  - `'name'`
 *  - `'mediaConfig.srtp'`
 *  - `'routingConfig.endpoints'`            (array-level custom error)
 *  - `'routingConfig.endpoints.0.host'`     (per-item error)
 */
export function flattenErrorPaths(
  errors: FieldErrors<FieldValues> | undefined,
): string[] {
  const paths: string[] = [];

  const walk = (node: unknown, prefix: string): void => {
    if (node === undefined || node === null) return;

    // Leaf error object: record its path and stop descending (do not walk `ref`).
    if (isFieldErrorLeaf(node)) {
      if (prefix) paths.push(prefix);
      return;
    }

    if (Array.isArray(node)) {
      node.forEach((child, index) => {
        walk(child, prefix ? `${prefix}.${index}` : String(index));
      });
      return;
    }

    if (isPlainRecord(node)) {
      for (const [key, child] of Object.entries(node)) {
        walk(child, prefix ? `${prefix}.${key}` : key);
      }
    }
  };

  walk(errors, '');
  return paths;
}

/**
 * Human-readable message for a submit-time error banner. ZodError.message is a
 * raw JSON dump of the issues array (zod v4), so format issues as
 * `path: message` lines instead of showing JSON to the user.
 */
export function describeSubmitError(err: unknown, fallback: string): string {
  if (err instanceof z.ZodError) {
    return err.issues
      .map((issue) => {
        const path = issue.path.join('.');
        return path ? `${path}: ${issue.message}` : issue.message;
      })
      .join('; ');
  }
  return err instanceof Error ? err.message : fallback;
}

/** True when an error path equals or is nested under any of `fields`. */
function pathMatchesField(errorPath: string, field: string): boolean {
  return errorPath === field || errorPath.startsWith(`${field}.`);
}

/** True if any error in `errors` falls under one of the tab's field prefixes. */
export function tabHasError(
  errors: FieldErrors<FieldValues> | undefined,
  fields: ReadonlyArray<string>,
): boolean {
  if (fields.length === 0) return false;
  const errorPaths = flattenErrorPaths(errors);
  return errorPaths.some((errorPath) =>
    fields.some((field) => pathMatchesField(errorPath, field)),
  );
}

/**
 * Id of the first visible tab (in array order) that owns an error path, or
 * `undefined` when no visible tab has an error. Used to focus the first
 * offending tab on submit. Hidden tabs are skipped because the user cannot
 * navigate to them.
 */
export function firstTabWithError<T extends ErrorMappableTab>(
  errors: FieldErrors<FieldValues> | undefined,
  tabs: ReadonlyArray<T>,
): string | undefined {
  const errorPaths = flattenErrorPaths(errors);
  if (errorPaths.length === 0) return undefined;
  const match = tabs.find((tab) =>
    !tab.hidden &&
    errorPaths.some((errorPath) =>
      tab.fields.some((field) => pathMatchesField(errorPath, field)),
    ),
  );
  return match?.id;
}
